import { Component, ChangeDetectionStrategy, OnInit, inject, signal, computed, output, input, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { forkJoin } from 'rxjs';
import { FieldsService } from '../../../services/fields.service';
import { Field, FieldCreateDTO, StructureType } from '../../../types/fields';
import { OcrPrimeStore } from '../../../store/ocr-prime.store';
import { FieldCardComponent } from './field-card.component';
import { FieldDialogComponent, FieldDialogData, FieldDialogResult } from './field-dialog.component';
import { ImportDialogComponent, ImportDialogResult } from './import-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-fields-panel',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
    MatTabsModule,
    FieldCardComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4 h-full">
      <!-- Fields Header -->
      <div class="flex items-center justify-between pb-2 border-b border-zinc-200 dark:border-zinc-700">
        <div class="flex items-center gap-2">
          <mat-icon class="text-blue-600 !text-xl">storage</mat-icon>
          <h2 data-testid="fields-panel-heading" class="text-lg font-semibold text-zinc-900 dark:text-white">Fields</h2>
          <span data-testid="fields-panel-count" class="ml-2 rounded-full bg-zinc-100 dark:bg-zinc-700 px-2.5 py-0.5 text-xs font-medium text-zinc-600 dark:text-zinc-300">
            {{ fields().length }}
          </span>
        </div>
        <div class="flex items-center gap-3">
          <button
            data-testid="fields-panel-import-btn"
            mat-mini-fab
            matTooltip="Import from JSON"
            (click)="openImportDialog()"
            class="!text-zinc-500 hover:!text-zinc-700 hover:!bg-zinc-100 dark:!text-zinc-400 dark:hover:!text-zinc-200 dark:!bg-zinc-700 dark:hover:!bg-zinc-600 !w-8 !h-8 !bg-zinc-50"
          >
            <mat-icon class="!text-lg">upload</mat-icon>
          </button>
          <button
            data-testid="fields-panel-export-btn"
            mat-mini-fab
            matTooltip="Export to Clipboard"
            (click)="exportToClipboard()"
            [disabled]="fields().length === 0"
            class="!text-zinc-500 hover:!text-zinc-700 hover:!bg-zinc-100 dark:!text-zinc-400 dark:hover:!text-zinc-200 dark:!bg-zinc-700 dark:hover:!bg-zinc-600 disabled:!text-zinc-300 !w-8 !h-8 !bg-zinc-50"
          >
            <mat-icon class="!text-lg">download</mat-icon>
          </button>
          <button
            data-testid="fields-panel-add-field-btn"
            mat-flat-button
            color="primary"
            class="gap-1.5 shadow-sm !h-8 !text-sm"
            (click)="openCreateDialog()"
          >
            <mat-icon class="!text-lg">add</mat-icon>
            Add Field
          </button>
        </div>
      </div>

      <!-- Search -->
      <div class="relative">
        <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500 !text-lg pointer-events-none">search</mat-icon>
        <input
          data-testid="fields-panel-search-input"
          type="text"
          placeholder="Search fields..."
          [value]="searchTerm()"
          (input)="onSearchChange($event)"
          class="w-full pl-10 pr-4 py-2 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:placeholder:text-zinc-500"
        >
      </div>

      <!-- Category Tabs -->
      @if (tabs().length > 1) {
        <mat-tab-group (selectedIndexChange)="onTabChange($event)" class="category-tabs">
          @for (tab of tabs(); track tab.value) {
            <mat-tab [label]="tab.label"></mat-tab>
          }
        </mat-tab-group>
      }

      <!-- Fields List -->
      @if (loadingFields()) {
        <div class="flex justify-center py-16">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else if (filteredFields().length === 0) {
        <div class="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800/50 p-8 text-center h-64">
          <div class="mb-3 rounded-full bg-zinc-100 dark:bg-zinc-700 p-3">
            <mat-icon class="text-zinc-400 dark:text-zinc-500 !text-2xl !w-7 !h-7 !leading-7">storage</mat-icon>
          </div>
          @if (searchTerm()) {
            <h3 data-testid="fields-panel-no-results" class="text-sm font-medium text-zinc-900 dark:text-zinc-100">No fields found</h3>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 max-w-[200px]">
              No fields match "{{ searchTerm() }}".
            </p>
            <button
              mat-stroked-button
              class="mt-4 !text-sm"
              (click)="clearSearch()"
            >
              Clear search
            </button>
          } @else {
            <h3 data-testid="fields-panel-empty" class="text-sm font-medium text-zinc-900 dark:text-zinc-100">No fields yet</h3>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 max-w-[220px]">Fields define the data points OCR extracts from documents — start here, then group them into structures.</p>
            <button
              mat-stroked-button
              class="mt-4 !text-sm"
              (click)="openCreateDialog()"
            >
              Add Field
            </button>
          }
        </div>
      } @else {
        <div class="flex-1 space-y-2 overflow-y-auto max-h-[calc(100vh-400px)] pr-1 -mr-1">
          @for (field of filteredFields(); track field.id) {
            <app-field-card
              class="block"
              [field]="field"
              [allFields]="fields()"
              [structures]="structures()"
              [highlightColor]="highlightColorFor(field)"
              [isDeleting]="deletingFieldId() === field.id"
              (edit)="openEditDialog($event)"
              (clone)="openCloneDialog($event)"
              (delete)="deleteField($event)"
            />
          }
        </div>
        <div class="text-center text-xs text-zinc-400 dark:text-zinc-500 pt-1">
          {{ filteredFields().length }} {{ filteredFields().length === 1 ? 'field' : 'fields' }}
          @if (searchTerm()) {
            <span> matching "{{ searchTerm() }}"</span>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `]
})
export class FieldsPanelComponent implements OnInit {
  private fieldsService = inject(FieldsService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  readonly store = inject(OcrPrimeStore);

  /** When set, the panel will auto-open the edit dialog for this field once loaded. */
  autoEditFieldId = input<string | null>(null);

  /** Structure currently hovered in the sibling panel — its member fields get a colored highlight. */
  hoveredStructure = input<StructureType | null>(null);

  fields = computed(() => this.store.fields());
  structures = computed(() => this.store.structures());
  loadingFields = computed(() => this.store.fieldsLoading());
  searchTerm = signal('');
  selectedCategory = signal<string>('all');

  categories = computed(() =>
    [...new Set(this.fields().map(f => f.category).filter(Boolean))].sort()
  );

  tabs = computed(() => [
    { label: 'All', value: 'all' },
    ...this.categories().map(c => ({ label: c, value: c }))
  ]);

  fieldsChanged = output<Field[]>();
  deletingFieldId = signal<string | null>(null);

  private autoEditTriggered = false;

  filteredFields = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const category = this.selectedCategory();
    let result = this.fields();

    if (category !== 'all') {
      result = result.filter(f => f.category === category);
    }

    if (term) {
      result = result.filter(f =>
        f.name.toLowerCase().includes(term) ||
        f.description?.toLowerCase().includes(term)
      );
    }

    return result;
  });

  constructor() {
    effect(() => {
      const id = this.autoEditFieldId();
      const list = this.fields();
      if (id && list.length > 0 && !this.autoEditTriggered) {
        const target = list.find(f => f.id === id);
        if (target) {
          this.autoEditTriggered = true;
          setTimeout(() => this.openEditDialog(target), 0);
        }
      }
    });
    // Emit fieldsChanged whenever store fields change
    effect(() => {
      this.fieldsChanged.emit(this.store.fields());
    });
  }

  ngOnInit(): void {
    this.loadFields();
  }

  loadFields(): void {
    this.store.loadFields(true);
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  clearSearch(): void {
    this.searchTerm.set('');
  }

  onTabChange(index: number): void {
    this.selectedCategory.set(this.tabs()[index]?.value ?? 'all');
  }

  highlightColorFor(field: Field): string | null {
    const hovered = this.hoveredStructure();
    if (!hovered || !hovered.fieldIds.includes(field.id)) return null;
    return hovered.color;
  }

  openCreateDialog(): void {
    this.openFieldDialog({ mode: 'create', allFields: this.fields() });
  }

  openEditDialog(field: Field): void {
    this.openFieldDialog({ mode: 'edit', field, allFields: this.fields() });
  }

  openCloneDialog(field: Field): void {
    this.openFieldDialog({ mode: 'clone', field, allFields: this.fields() });
  }

  private openFieldDialog(data: FieldDialogData): void {
    // Right-side drawer: keeps the field list visible behind the editor for context.
    const dialogRef = this.dialog.open(FieldDialogComponent, {
      data,
      width: 'min(640px, 95vw)',
      maxWidth: '95vw',
      height: '100dvh',
      maxHeight: '100dvh',
      position: { right: '0', top: '0' },
      panelClass: 'field-drawer-panel'
    });

    dialogRef.afterClosed().subscribe((result: FieldDialogResult | undefined) => {
      if (!result || result.action === 'cancel') return;

      if (data.mode === 'edit' && result.fieldId && result.data) {
        this.fieldsService.updateField(result.fieldId, result.data).subscribe({
          next: () => {
            this.snackBar.open('Field updated successfully', 'Close', { duration: 2000 });
            this.loadFields();
          },
          error: (err) => {
            console.error('Error updating field:', err);
            this.snackBar.open('Error updating field', 'Close', { duration: 3000 });
          }
        });
      } else if (result.data) {
        this.fieldsService.createField(result.data).subscribe({
          next: () => {
            const message = data.mode === 'clone' ? 'Field cloned successfully' : 'Field created successfully';
            this.snackBar.open(message, 'Close', { duration: 2000 });
            this.loadFields();
          },
          error: (err) => {
            console.error('Error creating field:', err);
            this.snackBar.open('Error creating field', 'Close', { duration: 3000 });
          }
        });
      }
    });
  }

  deleteField(field: Field): void {
    const usedIn = this.structures().filter(s => s.fieldIds.includes(field.id));
    const consequences: string[] = [];
    if (usedIn.length > 0) {
      consequences.push(`This field is used in ${usedIn.length} structure(s): ${usedIn.map(s => s.name).join(', ')}.`);
    }
    if (field.category === 'anchor') {
      consequences.push('This is an anchor field — deleting it affects document-to-template matching for every template.');
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: `Delete field "${field.name}"?`,
        message: 'This action cannot be undone.',
        consequences: consequences.length ? consequences : undefined,
        confirmLabel: 'Delete Field'
      } as ConfirmDialogData
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.deletingFieldId.set(field.id);
      this.fieldsService.deleteField(field.id).subscribe({
        next: () => {
          this.snackBar.open('Field deleted successfully', 'Close', { duration: 2000 });
          this.store.removeFieldLocally(field.id);
          this.deletingFieldId.set(null);
        },
        error: (err) => {
          console.error('Error deleting field:', err);
          this.snackBar.open(`Error deleting field, ${err?.error?.message || 'Unknown error'}`, 'Close', { duration: 3000 });
          this.deletingFieldId.set(null);
        }
      });
    });
  }

  async exportToClipboard(): Promise<void> {
    const fieldsToExport = this.fields().map(field => ({
      id: field.id,
      name: field.name,
      category: field.category,
      type: field.type,
      required: field.required,
      isAlwaysVisible: field.isAlwaysVisible,
      description: field.description || '',
      enumValues: field.enumValues || [],
      defaultValue: field.defaultValue,
      ...(field.fieldIds?.length ? { fieldIds: field.fieldIds } : {}),
      ...(typeof field.minTolerance === 'number' ? { minTolerance: field.minTolerance } : {})
    } as FieldCreateDTO));

    try {
      const json = JSON.stringify(fieldsToExport, null, 2);
      await navigator.clipboard.writeText(json);
      this.snackBar.open(`${fieldsToExport.length} field(s) copied to clipboard`, 'Close', { duration: 2000 });
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      this.snackBar.open('Error copying to clipboard', 'Close', { duration: 3000 });
    }
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open(ImportDialogComponent, {
      width: '500px',
      panelClass: 'field-dialog-panel'
    });

    dialogRef.afterClosed().subscribe((result: ImportDialogResult | undefined) => {
      if (!result || result.action === 'cancel' || !result.fields?.length) return;

      const createRequests = result.fields.map(field =>
        this.fieldsService.createField(field)
      );

      forkJoin(createRequests).subscribe({
        next: () => {
          this.snackBar.open(`${result.fields!.length} field(s) imported successfully`, 'Close', { duration: 2000 });
          this.loadFields();
        },
        error: (err) => {
          console.error('Error importing fields:', err);
          this.snackBar.open('Error importing fields. Some fields may have been created.', 'Close', { duration: 3000 });
          this.loadFields();
        }
      });
    });
  }
}
